import React, { useEffect, useRef, useState } from "react";
import { FiArrowRight, FiBookOpen, FiBriefcase, FiCheck, FiChevronDown, FiFileText, FiFolder, FiGrid, FiLayers } from "react-icons/fi";
import DealDeskGuide from "./DealDeskGuide";
import DealDeskToolbarContext from './DealDeskToolbarContext';
import "../../Components/css/DealDeskWorkspace.css";
import "./DealDeskShell.guide.css";

export const DEAL_DESK_APPS = [
  { id: "documentation", label: "Documentation", shortLabel: "Docs", icon: FiBookOpen },
  { id: "sow", label: "SOW", shortLabel: "SOW", icon: FiFileText },
  { id: "ideas", label: "Idea Builder", shortLabel: "Ideas", icon: FiLayers },
  { id: "projects", label: "Project Management", shortLabel: "Projects", icon: FiBriefcase },
  { id: "assets", label: "Assets", shortLabel: "Assets", icon: FiFolder },
];

export const workspaceAppFromUrl = () => {
  const app = new URLSearchParams(window.location.search).get("app");
  return DEAL_DESK_APPS.some(item => item.id === app) ? app : "home";
};

export default function DealDeskShell({ activeApp, children, onAppChange, projectName, projectLogo, actions, customTools = false, documentTools = false, documentGuide = false }) {
  const currentApp = DEAL_DESK_APPS.find(app => app.id === activeApp);
  const [switching, setSwitching] = useState(false);
  const [toolsTarget, setToolsTarget] = useState(null);
  const switcher = useRef(null), trigger = useRef(null);
  const CurrentIcon = currentApp?.icon || FiGrid;
  useEffect(() => { setSwitching(false); }, [activeApp]);
  useEffect(() => {
    if (!switching) return undefined;
    const outside = event => { if (!document.querySelector('dialog[open]') && !switcher.current?.contains(event.target)) setSwitching(false); };
    const escape = event => {
      if (event.key === 'Escape' && !document.querySelector('dialog[open]')) {
        event.preventDefault(); event.stopImmediatePropagation(); setSwitching(false); trigger.current?.focus();
      }
    };
    const frameFocus = () => { if (document.activeElement?.tagName === 'IFRAME') setSwitching(false); };
    document.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', escape, true); window.addEventListener('blur', frameFocus);
    return () => { document.removeEventListener('pointerdown', outside); window.removeEventListener('keydown', escape, true); window.removeEventListener('blur', frameFocus); };
  }, [switching]);
  const pick = app => { setSwitching(false); onAppChange(app); trigger.current?.focus(); };
  const menuKeys = event => {
    if (!switching || !switcher.current?.contains(event.target) || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const buttons = [...switcher.current.querySelectorAll('nav button')];
    const index = buttons.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1
      : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };
  return <DealDeskToolbarContext.Provider value={toolsTarget}><div className={`sow-workspace-shell deal-desk deal-desk--guided${customTools ? ' deal-desk--app-tools' : ''}${documentTools ? ' deal-desk--document-tools' : ''}`}>
    <a href="#deal-desk-content" className="deal-desk__skip">Skip to workspace</a>
    <header className="deal-desk__header">
      <div className="deal-desk__identity">
        <img className="deal-desk__brand" src="/assets/antrazal-brand.svg" alt="Antrazal" />
        <div className="deal-desk__context" title={projectName}>{projectLogo || <strong>{projectName}</strong>}</div>
      </div>
      <div className="deal-desk__switcher" ref={switcher} onKeyDown={menuKeys}>
        <button ref={trigger} type="button" className="deal-desk__switch-trigger" aria-label="Switch app" title="Switch app"
          aria-expanded={switching} aria-controls="deal-desk-app-menu" onClick={() => setSwitching(value => !value)}
          onKeyDown={event => { if (!switching && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); event.stopPropagation(); setSwitching(true); requestAnimationFrame(() => switcher.current?.querySelector('nav button')?.focus()); } }}>
          <CurrentIcon aria-hidden="true" /><span className="deal-desk__switch-name">{currentApp?.label || "Workspace"}</span>
          <span className="deal-desk__switch-short">{currentApp?.shortLabel || "Apps"}</span><FiChevronDown className="deal-desk__switch-chevron" aria-hidden="true" />
        </button>
        <nav id="deal-desk-app-menu" className="deal-desk__app-menu" aria-label="Switch workspace app" hidden={!switching}>
          <div className="deal-desk__menu-project">{projectName} workspace</div>
          <button type="button" aria-current={!currentApp ? 'page' : undefined} onClick={() => pick('home')}><FiGrid /><span>All apps</span>{!currentApp && <FiCheck />}</button>
          {DEAL_DESK_APPS.map(({ id, label, icon: Icon }) => <button key={id} type="button" aria-current={activeApp === id ? 'page' : undefined} onClick={() => pick(id)}><Icon /><span>{label}</span>{activeApp === id && <FiCheck />}</button>)}
        </nav>
      </div>
      <div className="deal-desk__tools" ref={setToolsTarget}>
        {currentApp && !customTools && !documentGuide && <DealDeskGuide key={currentApp.id} app={currentApp} iconOnly />}
      </div>
      {actions}
    </header>
    <div className="deal-desk__content" id="deal-desk-content" tabIndex={-1}>
      {activeApp === "home" && <main className="deal-desk__launcher">
        <header><h1>{projectName} workspace</h1></header>
        <nav className="deal-desk__app-grid" aria-label="Workspace apps">
          {DEAL_DESK_APPS.map(({ id, label, icon: Icon }) => <button key={id} type="button"
            className={`deal-desk__launch-app deal-desk__launch-app--${id}`} onClick={() => onAppChange(id)}>
            <span className="deal-desk__launch-icon"><Icon aria-hidden="true" /></span>
            <span className="deal-desk__launch-label">{label}</span><FiArrowRight className="deal-desk__launch-arrow" aria-hidden="true" />
          </button>)}
        </nav>
      </main>}
      {children}
    </div>
  </div></DealDeskToolbarContext.Provider>;
}
